import { z } from 'zod';

// ============================================================================
// TEMPLATE SCHEMAS
// ============================================================================

export const FieldHighlight = z.object({
  isHighlighted: z.boolean(),
  reason: z.string().optional(),
  previousValue: z.unknown().optional(),
  context: z.string().optional(),
});

export const TemplateEditingState = z.enum(['editable', 'locked', 'computed']);

export const TemplateEditingMetadata = z.object({
  state: TemplateEditingState.optional(),
  reason: z.string().optional(),
});

/**
 * Field definition in a template - defines structure without values
 */
export const FieldDefinition = z.object({
  id: z.string(), // Human-readable: "fld_ragione-sociale"
  label: z.string(), // Display name, can use {{}} templates
  type: z.enum(['text', 'number', 'date', 'bool', 'choice']),
  format: z.enum(['multiline', 'email']).optional(), // Presentation hints

  // Data binding
  source: z.string().optional(), // e.g., "{{company.name}}" - auto-populate
  disableHighlight: z.boolean().optional(),

  // Validation
  required: z.boolean().optional(),
  options: z
    .array(
      z.object({
        value: z.string(),
        label: z.string().optional(),
      })
    )
    .optional(), // For choice type
  validation: z
    .object({
      min: z.number().optional(),
      max: z.number().optional(),
      pattern: z.string().optional(),
    })
    .optional(),

  // Formatting (locale-aware display)
  formatting: z
    .object({
      locale: z.string().optional(), // Override template locale (BCP-47 format)
      display: z.enum(['default', 'month-year']).optional(), // Date display mode
      labels: z
        .object({
          true: z.string().optional(),
          false: z.string().optional(),
          null: z.string().optional(),
        })
        .optional(), // Boolean labels
      maximumFractionDigits: z.number().optional(), // Number precision
      unit: z.string().optional(), // Display unit (e.g., "kg", "bar")
    })
    .optional(),

  // UI hints
  placeholder: z.string().optional(),
  hint: z.string().optional(),
  width: z.string().optional(),

  // Audit trail (for P1 template editing)
  _idHistory: z
    .object({
      derivedFrom: z.string(), // Original label
      generatedAt: z.string(),
      lastLabelUpdate: z.string().optional(),
    })
    .optional(),

  // Editing metadata (read-only/computed flags)
  editing: TemplateEditingMetadata.optional(),
});

/**
 * Row definition inside a section
 */
export const TemplateRow = z.object({
  id: z.string(), // Human-readable: "row_societa"
  label: z.string(), // Display name for the grouped row
  fieldIds: z.array(z.string()).default([]),
  editing: TemplateEditingMetadata.optional(),
  layout: z
    .object({
      columns: z.number().int().min(1).optional(),
    })
    .optional(),
});

const TemplateTableColumn = z.object({
  id: z.string(),
  label: z.string(),
  fieldPath: z.string(),
  align: z.enum(['left', 'center', 'right']).optional(),
  width: z.string().optional(),
});

export const TemplateViewBlockTable = z.object({
  type: z.literal('table'),
  id: z.string(),
  columns: z.array(TemplateTableColumn).min(1),
  rowSource: z.string(),
  emptyMessage: z.string().optional(),
  headerStyle: z.enum(['default', 'bold', 'subtle']).optional(),
});

export const TemplateViewBlockImage = z.object({
  type: z.literal('image'),
  source: z.string().optional(),
  alt: z.string().optional(),
  maxWidthCm: z.number().optional(),
  maxHeightCm: z.number().optional(),
  align: z.enum(['left', 'center', 'right']).optional(),
  fallbackText: z.string().optional(),
});

const TemplateScorecardSeries = z.object({
  id: z.string(),
  label: z.string(),
  fieldId: z.string(),
  color: z.string().optional(),
});

export const TemplateViewBlockScorecardChart = z.object({
  type: z.literal('scorecard-chart'),
  title: z.string(),
  noteFieldId: z.string(),
  dateFieldId: z.string(),
  maxPoints: z.number().int().min(1).optional(),
  series: z.array(TemplateScorecardSeries).min(1),
});

export const TemplateViewBlockSectionContent = z.object({
  type: z.literal('section-content'),
});

export const TemplateViewBlockGrid = z.object({
  type: z.literal('grid'),
  columns: z.array(
    z.object({
      width: z.string(),
      blocks: z.array(z.unknown()), // TemplateViewBlock[] (recursive)
    })
  ),
});

export const TemplateViewBlock = z.discriminatedUnion('type', [
  TemplateViewBlockTable,
  TemplateViewBlockImage,
  TemplateViewBlockScorecardChart,
  TemplateViewBlockSectionContent,
  TemplateViewBlockGrid,
]);

/**
 * Optional navigation metadata exposed to the UI for grouping sections
 */
export const TemplateGroup = z.object({
  id: z.string(),
  label: z.string(),
  order: z.number().int(),
  icon: z.string().optional(),
  description: z.string().optional(),
});

/**
 * Section definition in a template
 */
export const TemplateSection = z.object({
  id: z.string(), // Human-readable: "sec_societa", "sec_unita-frigorifere"
  name: z.string(), // Display name, can use {{}} templates
  type: z.enum(['static', 'repeating']),
  visibleIf: z.string().optional(),
  navigationGroup: TemplateGroup.optional(),
  editing: TemplateEditingMetadata.optional(),

  // For repeating sections (e.g., one per refrigeration unit)
  repeat: z
    .object({
      forEach: z.string(), // Path like "site.refrigerationUnits"
      as: z.string(), // Iterator variable like "unit"
    })
    .optional(),

  // Field definitions (NO VALUES)
  fields: z.array(FieldDefinition).default([]),

  // Presentation hints
  layout: z
    .object({
      columns: z.number().int().min(1).optional(),
      ui: z
        .object({
          groupId: z.string().optional(),
          groupTitle: z.string().optional(),
          variant: z.enum(['table', 'form', 'checklist']).optional(),
        })
        .optional(),
    })
    .optional(),

  overviewNote: z.string().optional(),
  rows: z.array(TemplateRow).default([]),
  viewBlocks: z.array(TemplateViewBlock).default([]),
  viewBlocksMode: z.enum(['append', 'replace']).optional(),
  disableHighlight: z.boolean().optional(),
});

/**
 * Report template - structure only, no values
 */
export const ReportTemplate = z.object({
  id: z.string(),
  version: z.string(), // "2025-09-27-v2"
  name: z.string(),
  description: z.string().optional(),
  groups: z.array(TemplateGroup).optional(),
  sections: z.array(TemplateSection),
  metadata: z.object({
    targetType: z.literal('ammonia-audit'),
    locale: z.string().default('it-IT'), // BCP-47 locale for formatting
    createdAt: z.string(),
    updatedAt: z.string(),
  }),
});

// Export types
export type FieldHighlight = z.infer<typeof FieldHighlight>;
export type TemplateEditingState = z.infer<typeof TemplateEditingState>;
export type TemplateEditingMetadata = z.infer<typeof TemplateEditingMetadata>;
export type FieldDefinition = z.infer<typeof FieldDefinition>;
export type TemplateRow = z.infer<typeof TemplateRow>;
export type TemplateGroup = z.infer<typeof TemplateGroup>;
export type TemplateSection = z.infer<typeof TemplateSection>;
export type ReportTemplate = z.infer<typeof ReportTemplate>;
export type TemplateViewBlockTable = z.infer<typeof TemplateViewBlockTable>;
export type TemplateViewBlock = z.infer<typeof TemplateViewBlock>;
export type TemplateViewBlockImage = z.infer<typeof TemplateViewBlockImage>;
export type TemplateViewBlockScorecardChart = z.infer<typeof TemplateViewBlockScorecardChart>;
export type TemplateViewBlockSectionContent = z.infer<typeof TemplateViewBlockSectionContent>;
export type TemplateViewBlockGrid = z.infer<typeof TemplateViewBlockGrid>;
